import { PlusIcon, CalendarIcon, ClockIcon, TruckIcon, UserIcon } from "@heroicons/react/24/solid";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";

export default function AddPermitPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isIndefinite, setIsIndefinite] = useState(false);
  const [isFuturePermit, setIsFuturePermit] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [formData, setFormData] = useState({
    reference_label: "",
    vrm: "",
    make: "",
    model: "",
    colour: "",
    driver_first: "",
    driver_last: "",
    end_ts: "",
    start_ts: ""
  });
  const navigate = useNavigate();

  // Calculate minimum date (current time + 1 minute to ensure it's at least 1 minute in the future)
  const minDate = new Date(Date.now() + 1 * 60 * 1000).toISOString().slice(0, 16);

  // Fetch site settings on component mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/site-settings/');
        setSiteSettings(response.data);
      } catch (error) {
        // Handle error silently
        // Default to not allowing manual reference if we can't fetch settings
        setSiteSettings({ can_add_reference: false });
      }
    };
    
    fetchSiteSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert VRM to uppercase as user types
    const processedValue = name === 'vrm' ? value.toUpperCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Check acknowledgement first
    if (!acknowledged) {
      setError("Please tick the acknowledgement checkbox at the bottom of the page that you have verified your details before creating the permit.");
      // Scroll to top to show the error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setLoading(true);

    try {
      // Prepare submission data
      const submitData = {
        ...formData,
        end_ts: siteSettings?.default_end_date_limit_hours > 0 ? null : (isIndefinite ? null : formData.end_ts),
        start_ts: isFuturePermit ? formData.start_ts : ''  // Empty for immediate permits - backend will use timezone.now()
      };

      // If site doesn't allow manual reference numbers, remove reference_label from submission
      if (siteSettings && !siteSettings.can_add_reference) {
        delete submitData.reference_label;
      }

      // Validate expiry date if not indefinite and site allows custom end dates
      if (!siteSettings?.default_end_date_limit_hours && !isIndefinite) {
        const selectedDate = new Date(formData.end_ts);
        if (selectedDate <= new Date()) {
          setError('Expiry date must be in the future');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setLoading(false);
          return;
        }
      }

      // Validate scheduled start date if it's a future permit
      if (isFuturePermit && formData.start_ts && siteSettings?.can_schedule_permits) {
        const scheduledDate = new Date(formData.start_ts);
        const currentDate = new Date();
        
        if (scheduledDate <= currentDate) {
          setError('Scheduled start date must be in the future');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setLoading(false);
          return;
        }
        
        // Check if scheduled date is at least 1 minute in the future
        const minScheduledTime = new Date(currentDate.getTime() + 1 * 60 * 1000); // 1 minute
        if (scheduledDate <= minScheduledTime) {
          setError('Scheduled start date must be at least 1 minute in the future');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setLoading(false);
          return;
        }
      }
      
      const response = await axiosInstance.post('/api/v1/permits/', submitData);
      
      // Handle 202 Accepted response for async operations
      if (response.status === 202 || response.status === 201) {
        navigate('/my-permits', { state: { shouldRefresh: true } });
      }
    } catch (error) {
      // More user-friendly error messages
      let userFriendlyError = "Failed to create permit. Please try again.";
      
      // Handle Django REST Framework validation errors
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for non-field errors first (general validation errors)
        if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          userFriendlyError = errorData.non_field_errors[0];
        }
        // Check for specific field errors
        else if (errorData.reference_label) {
          userFriendlyError = errorData.reference_label[0];
        } else if (errorData.vrm) {
          userFriendlyError = errorData.vrm[0];
        } else if (errorData.end_ts) {
          userFriendlyError = errorData.end_ts[0];
        } else if (errorData.start_ts) {
          userFriendlyError = errorData.start_ts[0];
        } else if (errorData.make) {
          userFriendlyError = errorData.make[0];
        } else if (errorData.model) {
          userFriendlyError = errorData.model[0];
        } else if (errorData.driver_first) {
          userFriendlyError = errorData.driver_first[0];
        } else if (errorData.driver_last) {
          userFriendlyError = errorData.driver_last[0];
        }
        // Check for legacy error format (single error field)
        else if (errorData.error) {
          // Check if it's a ZatPark sync error
          if (errorData.error.includes('Failed to sync with ZatPark')) {
            userFriendlyError = "Unable to create permit at this time. Please try again later.";
          } else if (errorData.error.includes('An active permit already exists with reference number')) {
            // Display the specific error for duplicate reference + vrm
            userFriendlyError = errorData.error;
          } else if (errorData.error.includes('A permit for this VRM and time already exists')) {
            // Display the specific error for overlapping VRM permits
            userFriendlyError = "Overlapping permit detected — adjust or cancel the other permit first.";
          } else if (errorData.error.includes('You have reached your permit limit')) {
            // Display the specific error for permit limit reached
            userFriendlyError = errorData.error;
          } else {
            // For other errors, use the error message but remove technical details
            userFriendlyError = errorData.error
              .replace(/Failed to sync with ZatPark for permit '.*?': /, '')
              .replace(/service is unavailable \d+/, 'service is currently unavailable');
          }
        }
      }
      
      setError(userFriendlyError);
      // Scroll to top to show the error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching site settings
  if (!siteSettings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-customIndigo mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Add New Permit</h1>
                <p className="mt-2 text-lg text-gray-600">
                  Create a new permit for a visitor
                </p>
              </div>
              <Link 
                to="/my-permits" 
                className="inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 transition-colors self-start sm:self-auto"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Permits
              </Link>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium">Creation Failed</p>
                  <p className="mt-1 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Section - Only show if site allows manual reference */}
            {siteSettings?.can_add_reference && (
              <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-customIndigo/10 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-customIndigo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="reference_label" className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Label <span className="text-red-500">*</span>
                    </label>
                    {/* Show dropdown if user has predefined reference options, otherwise show free text input */}
                    {siteSettings?.reference_options?.length > 0 ? (
                      <>
                        <select
                          id="reference_label"
                          name="reference_label"
                          value={formData.reference_label}
                          onChange={handleChange}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200 bg-white"
                          required
                        >
                          <option value="">Select a reference label</option>
                          {siteSettings.reference_options.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <p className="mt-2 text-xs text-gray-500 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Select a reference label for this permit
                        </p>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          id="reference_label"
                          name="reference_label"
                          value={formData.reference_label}
                          onChange={handleChange}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                          required
                          maxLength={50}
                          placeholder="Enter reference label (e.g., 'Bay 1')"
                        />
                        <p className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                          <span className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Enter a unique reference label for this permit
                          </span>
                          <span className="text-gray-400">
                            {formData.reference_label.length}/50
                          </span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Vehicle Details Section */}
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <TruckIcon className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Vehicle Details</h2>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="vrm" className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="vrm"
                    name="vrm"
                    value={formData.vrm}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    required
                    placeholder="e.g., AB12 CDE"
                  />
                </div>

                <div>
                  <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="make"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    required
                    placeholder="e.g., Toyota"
                  />
                </div>

                <div>
                  <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    required
                    placeholder="e.g., Corolla"
                  />
                </div>

                <div>
                  <label htmlFor="colour" className="block text-sm font-medium text-gray-700 mb-2">
                    Colour
                  </label>
                  <input
                    type="text"
                    id="colour"
                    name="colour"
                    value={formData.colour}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    placeholder="e.g., Red"
                  />
                </div>
              </div>
            </div>

            {/* Driver Details Section */}
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <UserIcon className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Driver Details</h2>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="driver_first" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="driver_first"
                    name="driver_first"
                    value={formData.driver_first}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    placeholder="e.g., John"
                  />
                </div>

                <div>
                  <label htmlFor="driver_last" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="driver_last"
                    name="driver_last"
                    value={formData.driver_last}
                    onChange={handleChange}
                    className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                    placeholder="e.g., Smith"
                  />
                </div>
              </div>
            </div>

            {/* Permit Details Section */}
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <CalendarIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Permit Details</h2>
              </div>
              
              {/* Future Permit Toggle - Only show if site allows scheduling */}
              {siteSettings?.can_schedule_permits && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="isFuturePermit"
                      checked={isFuturePermit}
                      onChange={(e) => {
                        setIsFuturePermit(e.target.checked);
                        if (!e.target.checked) {
                          setFormData(prev => ({ ...prev, start_ts: "" }));
                        }
                      }}
                      className="h-4 w-4 text-customIndigo focus:ring-customIndigo border-gray-300 rounded mt-0.5"
                    />
                    <div className="ml-3">
                      <label htmlFor="isFuturePermit" className="block text-sm font-semibold text-blue-900">
                        Schedule permit for future activation
                      </label>
                      <p className="text-xs text-blue-700 mt-1">
                        Enable this to create a permit that will activate at a specific date and time. Permits will be scheduled and can be activated later.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Scheduled Start Date - Only show if future permit is enabled and site allows scheduling */}
                {isFuturePermit && siteSettings?.can_schedule_permits && (
                  <div>
                    <label htmlFor="start_ts" className="block text-sm font-medium text-gray-700 mb-2">
                      Scheduled Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      id="start_ts"
                      name="start_ts"
                      value={formData.start_ts}
                      onChange={handleChange}
                      min={minDate}
                      className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                      required
                    />
                    <p className="mt-2 text-xs text-gray-500 flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      When the permit should become active
                    </p>
                  </div>
                )}

                {/* End Date Section - Only show if site doesn't have default end date limit */}
                {!siteSettings?.default_end_date_limit_hours && (
                  <>
                    <div>
                      <label htmlFor="end_ts" className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date {!isIndefinite && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="datetime-local"
                        id="end_ts"
                        name="end_ts"
                        value={formData.end_ts}
                        onChange={handleChange}
                        disabled={isIndefinite}
                        min={isFuturePermit && formData.start_ts ? formData.start_ts : minDate}
                        className={`block w-full rounded-xl border border-gray-300 px-4 py-3 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200 ${
                          isIndefinite ? 'bg-gray-100' : ''
                        }`}
                        required={!isIndefinite}
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <div className="flex items-start space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                        <input
                          type="checkbox"
                          id="isIndefinite"
                          checked={isIndefinite}
                          onChange={(e) => {
                            setIsIndefinite(e.target.checked);
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, end_ts: "" }));
                            }
                          }}
                          className="h-4 w-4 text-customIndigo focus:ring-customIndigo border-gray-300 rounded mt-0.5"
                        />
                        <div className="flex-1">
                          <label htmlFor="isIndefinite" className="block text-sm font-semibold text-orange-900">
                            Set as Indefinite
                          </label>
                          <p className="text-xs text-orange-700 mt-1">
                            Remove expiry date - permit will remain active until manually cancelled
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Default End Date Info - Show when site has default end date limit */}
                {siteSettings?.default_end_date_limit_hours > 0 && (
                  <div className="sm:col-span-2">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-semibold text-blue-900">
                            Automatic End Date
                          </h3>
                          <p className="text-xs text-blue-700 mt-1">
                            Permits will automatically expire {siteSettings.default_end_date_limit_hours} hour{siteSettings.default_end_date_limit_hours !== 1 ? 's' : ''} after the start time. You cannot modify the end date.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Acknowledgment Section */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-amber-800">
                    Important: Please verify your details and permit limits
                  </h3>
                  <div className="mt-2">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-1 h-4 w-4 text-customIndigo focus:ring-customIndigo border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm text-amber-700">
                        I confirm I have checked all details above, especially the <strong>vehicle registration number</strong>. 
                        I understand this permit will count towards my permit limits and cannot be reverted. 
                        I confirm this permit complies with my permit limits.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={loading || 
                  (siteSettings?.can_add_reference && !formData.reference_label) || 
                  (!siteSettings?.default_end_date_limit_hours && !isIndefinite && !formData.end_ts) ||
                  (isFuturePermit && siteSettings?.can_schedule_permits && !formData.start_ts)}
                className={`inline-flex justify-center items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl text-white transition-all duration-200 ${
                  loading || 
                  (siteSettings?.can_add_reference && !formData.reference_label) || 
                  (!siteSettings?.default_end_date_limit_hours && !isIndefinite && !formData.end_ts) ||
                  (isFuturePermit && siteSettings?.can_schedule_permits && !formData.start_ts)
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-customIndigo hover:bg-customIndigo/90 shadow-lg hover:shadow-xl'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Permit...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Create Permit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
